// NSARA Stores V2 — modale/drawer accessible partagée (création de boutique,
// création de produit, détail commande).
//
// Accessibilité (section 26 de la mission): fermeture sur Escape, focus initial
// dans la boîte, retour du focus sur l'élément déclencheur à la fermeture,
// piège à Tab, role="dialog" + aria-modal, clic sur le voile pour fermer,
// scroll du corps bloqué. Aucun `alert()` natif n'est utilisé dans Stores V2.
import React, { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export default function Modal({ open, onClose, labelledBy, describedBy, children, size = 'md' }) {
  const boxRef = useRef(null);
  const restoreRef = useRef(null);

  // `onClose` est presque toujours une fonction inline côté parent: sa référence
  // change à CHAQUE rendu. Si l'effet d'ouverture ci-dessous en dépendait, chaque
  // frappe dans un champ relancerait le cycle cleanup → setup de cet effet, donc
  // un retour du focus sur l'élément déclencheur puis un focus forcé sur le
  // PREMIER champ de la boîte: la saisie mobile sautait d'un champ à l'autre et
  // le clavier se fermait. On lit donc la dernière version via une ref, et
  // l'effet ne dépend que de `open`: le focus n'est déplacé qu'à l'ouverture.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusables = () => [...(boxRef.current?.querySelectorAll(FOCUSABLE) || [])]
      // Exclut les éléments réellement invisibles (display:none, ex. champ
      // anti-robot) — getComputedStyle fonctionne à la fois dans le navigateur
      // et sous jsdom, contrairement à offsetParent.
      .filter((el) => window.getComputedStyle(el).display !== 'none');
    // Laisse le rendu se faire avant de déplacer le focus.
    const focusTimer = window.setTimeout(() => { focusables()[0]?.focus(); }, 0);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      try { restoreRef.current?.focus?.(); } catch (e) { /* élément déclencheur démonté */ }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="stores-modal-root">
      <button type="button" className="stores-modal-backdrop" aria-label="Fermer" onClick={onClose} tabIndex={-1} />
      <div
        className={`stores-modal stores-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        ref={boxRef}
      >
        {children}
      </div>
    </div>
  );
}
